import { useCallback, useMemo } from 'react'
import { message, Table, Button, Divider, Popconfirm } from 'antd'
import {
  DeleteOutlined,
  EditOutlined,
  FolderAddOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import api from '../../api'
import { useAsync } from '../../hooks'
import useQueryParams from '../../hooks/useQueryParams'
import {
  useFetchPractices,
  usePractices,
  usePracticesPagination,
} from '../../redux/slices/practices'
import { toPath } from '../../routes'
import { ListError, PageHeader } from '../Common'
import AddNewItemButton from '../Common/AddNewItemButton'
import { buildUrl } from '../../api/api-client'

import Filters from '../Forms/Filters'
import CircleCheckIcon from '../Common/CircleCheckIcon'

const QUERY_PARAMS_SCHEMA = {
  page: Number,
  showInactive: Boolean,
}

const COLUMNS = [
  {
    title: 'Logo',
    dataIndex: 'logoPath',
    key: 'logoPath',
    render: (_text, record) =>
      record.logoPath && (
        <img
          width="80px"
          src={buildUrl(record.logoPath)}
          alt={`logo-${record.name}`}
        />
      ),
  },
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
  },
  {
    title: 'Address',
    dataIndex: 'address',
    key: 'address',
  },
  {
    title: 'Phone Number',
    dataIndex: 'phoneNumber',
    key: 'phoneNumber',
  },
  {
    title: 'Fax Number',
    dataIndex: 'faxNumber',
    key: 'faxNumber',
  },
]

export default function Practices() {
  const [conditions, setConditions] = useQueryParams({
    initialValues: { page: 1 },
    schema: QUERY_PARAMS_SCHEMA,
  })
  const navigate = useNavigate()
  const goToEdit = useCallback(
    id => navigate(toPath.practices.practice.edit({ id })),
    [navigate],
  )
  const {
    pending,
    error,
    clearError,
    execute: fetchPractices,
  } = useAsync(useFetchPractices(conditions, true))
  const handlePageChange = useCallback(
    page => setConditions({ ...conditions, page }),
    [conditions, setConditions],
  )
  const handleSubmit = useCallback(
    ({ query, showInactive }) => {
      setConditions({ page: 1, query, showInactive: showInactive || undefined })
    },
    [setConditions],
  )
  const { pending: deleting, execute: handleDelete } = useAsync(async id => {
    try {
      await api.practices.deletePractice(id)
      message.success('Practice successfully removed')
    } catch (e) {
      message.error(`There was an error removing this practice: ${e.message}`)
    }
    fetchPractices()
  }, false)
  const { pageSize, total } = usePracticesPagination()
  const practices = usePractices()

  const columns = useMemo(
    () => [
      ...COLUMNS,
      {
        align: 'center',
        title: 'Active?',
        dataIndex: 'active',
        key: 'active',
        render(_text, record) {
          return <CircleCheckIcon checked={record.active} />
        },
      },
      {
        align: 'center',
        title: 'Actions',
        key: 'actions',
        width: '100px',
        render: (_text, record) => (
          <span>
            <Button
              onClick={() => goToEdit(record.id)}
              type="link"
              icon={<EditOutlined />}
            />
            <Divider type="vertical" />
            <Popconfirm
              title="Are you sure you want to delete this practice?"
              onConfirm={() => handleDelete(record.id)}
              okText="Yes"
              cancelText="No"
              placement="topRight"
            >
              <Button
                type="link"
                icon={<DeleteOutlined />}
                className="delete-button"
              />
            </Popconfirm>
          </span>
        ),
      },
    ],
    [goToEdit, handleDelete],
  )

  return (
    <div>
      {error && <ListError description={error.message} onClose={clearError} />}
      <PageHeader
        title="Practices"
        extra={
          <AddNewItemButton
            itemName="practice"
            path={toPath.practices.new()}
            icon={<FolderAddOutlined />}
          />
        }
      />
      <Filters
        showSearch
        showInactiveCheck
        onSubmit={handleSubmit}
        currentValues={conditions}
      />

      <Table
        size="middle"
        rowKey="id"
        loading={pending || deleting}
        dataSource={practices}
        columns={columns}
        pagination={{
          current: conditions.page || 1,
          pageSize,
          onChange: handlePageChange,
          total,
        }}
      />
    </div>
  )
}
